import React, { useMemo, useState, Component } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { LayersIcon, SearchIcon, FilterIcon, XIcon, PlusIcon, EditIcon, TrashIcon, EyeIcon, CheckIcon, AlertCircleIcon, SaveIcon, ImageIcon } from 'lucide-react';
import { MOCK_IMAGE_SETS, MOCK_IMAGES } from '../schemas/mockData';
import type { ImageSet, Domain, Level, AgeBand, EmotionalCategory, ImageMetadata } from '../schemas/types';
import { SearchInput } from '../components/SearchInput';
/**
 * ImageSets Page
 * Manage collections of images organized by framework grid
 * Includes promotion/prevention filtering for emotional categorization
 */
export function ImageSets() {
  const {
    t
  } = useOrganization();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [selectedAgeBand, setSelectedAgeBand] = useState<AgeBand | 'all'>('all');
  const [selectedEmotionalCategory, setSelectedEmotionalCategory] = useState<EmotionalCategory | 'all'>('all');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedSet, setSelectedSet] = useState<ImageSet | null>(null);
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [editingSet, setEditingSet] = useState<ImageSet | null>(null);
  const [deletingSet, setDeletingSet] = useState<ImageSet | null>(null);
  // Local state for image sets (in production, this would be managed by a state management solution)
  const [imageSets, setImageSets] = useState<ImageSet[]>(MOCK_IMAGE_SETS);
  // Filter image sets
  const filteredSets = useMemo(() => {
    return imageSets.filter(set => {
      const matchesSearch = searchQuery === '' || set.name.toLowerCase().includes(searchQuery.toLowerCase()) || set.description?.toLowerCase().includes(searchQuery.toLowerCase()) || set.id.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = selectedStatus === 'all' || set.status === selectedStatus;
      const matchesAgeBand = selectedAgeBand === 'all' || set.ageBands.includes(selectedAgeBand);
      // Filter by emotional category - check if set contains images with the selected category
      let matchesEmotionalCategory = true;
      if (selectedEmotionalCategory !== 'all') {
        const setImages = MOCK_IMAGES.filter(img => set.images.includes(img.id));
        matchesEmotionalCategory = setImages.some(img => img.emotionalCategory === selectedEmotionalCategory);
      }
      return matchesSearch && matchesStatus && matchesAgeBand && matchesEmotionalCategory;
    });
  }, [searchQuery, selectedStatus, selectedAgeBand, selectedEmotionalCategory, imageSets]);
  // Calculate statistics
  const stats = useMemo(() => {
    const allSetImages = filteredSets.flatMap(set => MOCK_IMAGES.filter(img => set.images.includes(img.id)));
    return {
      totalSets: imageSets.length,
      filteredSets: filteredSets.length,
      published: filteredSets.filter(s => s.status === 'published').length,
      draft: filteredSets.filter(s => s.status === 'draft').length,
      totalImages: allSetImages.length,
      promotion: allSetImages.filter(img => img.emotionalCategory === 'promotion').length,
      prevention: allSetImages.filter(img => img.emotionalCategory === 'prevention').length
    };
  }, [filteredSets, imageSets]);
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('all');
    setSelectedAgeBand('all');
    setSelectedEmotionalCategory('all');
  };
  const hasActiveFilters = searchQuery !== '' || selectedStatus !== 'all' || selectedAgeBand !== 'all' || selectedEmotionalCategory !== 'all';
  // Get images for a specific set
  const getSetImages = (set: ImageSet) => {
    return MOCK_IMAGES.filter(img => set.images.includes(img.id));
  };
  // Calculate emotional category distribution for a set
  const getSetEmotionalDistribution = (set: ImageSet) => {
    const images = getSetImages(set);
    const promotion = images.filter(img => img.emotionalCategory === 'promotion').length;
    const prevention = images.filter(img => img.emotionalCategory === 'prevention').length;
    return {
      promotion,
      prevention,
      total: images.length
    };
  };
  // Calculate grid coverage for a set
  const getGridCoverage = (set: ImageSet) => {
    const domains: Domain[] = ['Self', 'Material', 'Social', 'Spiritual'];
    const levels: Level[] = ['Foundational', 'Experiential', 'Aspirational'];
    let filled = 0;
    let total = domains.length * levels.length;
    domains.forEach(domain => {
      levels.forEach(level => {
        if (set.grid[domain]?.[level]?.length > 0) {
          filled++;
        }
      });
    });
    return {
      filled,
      total,
      percentage: Math.round(filled / total * 100)
    };
  };
  // CRUD Handlers
  const handleCreateSet = (newSet: Omit<ImageSet, 'id' | 'createdAt' | 'updatedAt' | 'version'>) => {
    const imageSet: ImageSet = {
      ...newSet,
      id: `set-${Date.now()}`,
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setImageSets([...imageSets, imageSet]);
    setShowCreateModal(false);
  };
  const handleUpdateSet = (updatedSet: ImageSet) => {
    setImageSets(imageSets.map(set => set.id === updatedSet.id ? {
      ...updatedSet,
      updatedAt: new Date().toISOString()
    } : set));
    setShowEditModal(false);
    setEditingSet(null);
  };
  const handleDeleteSet = () => {
    if (deletingSet) {
      setImageSets(imageSets.filter(set => set.id !== deletingSet.id));
      setShowDeleteConfirm(false);
      setDeletingSet(null);
    }
  };
  const openEditModal = (set: ImageSet) => {
    setEditingSet(set);
    setShowEditModal(true);
  };
  const openDeleteConfirm = (set: ImageSet) => {
    setDeletingSet(set);
    setShowDeleteConfirm(true);
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-1362">
      {/* Header */}
      <div className="mb-8" data-id="element-1363">
        <div className="flex items-center justify-between mb-3" data-id="element-1364">
          <div className="flex items-center gap-3" data-id="element-1365">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg" data-id="element-1366">
              <LayersIcon className="w-7 h-7 text-white" data-id="element-1367" />
            </div>
            <div data-id="element-1368">
              <h1 className="text-3xl font-bold text-gray-900" data-id="element-1369">Image Sets</h1>
              <p className="text-gray-600 mt-1" data-id="element-1370">
                Manage collections with emotional categorization
              </p>
            </div>
          </div>
          <button onClick={() => setShowCreateModal(true)} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all font-semibold shadow-lg flex items-center gap-2" data-id="element-1371">
            <PlusIcon className="w-5 h-5" data-id="element-1372" />
            Create Set
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-7 gap-4 mb-6" data-id="element-1373">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border-2 border-blue-200" data-id="element-1374">
          <div className="text-sm font-semibold text-blue-900 mb-1" data-id="element-1375">
            Total Sets
          </div>
          <div className="text-2xl font-bold text-blue-700" data-id="element-1376">
            {stats.totalSets}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border-2 border-purple-200" data-id="element-1377">
          <div className="text-sm font-semibold text-purple-900 mb-1" data-id="element-1378">
            Filtered
          </div>
          <div className="text-2xl font-bold text-purple-700" data-id="element-1379">
            {stats.filteredSets}
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 border-2 border-green-200" data-id="element-1380">
          <div className="text-sm font-semibold text-green-900 mb-1" data-id="element-1381">
            Published
          </div>
          <div className="text-2xl font-bold text-green-700" data-id="element-1382">
            {stats.published}
          </div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4 border-2 border-orange-200" data-id="element-1383">
          <div className="text-sm font-semibold text-orange-900 mb-1" data-id="element-1384">
            Draft
          </div>
          <div className="text-2xl font-bold text-orange-700" data-id="element-1385">
            {stats.draft}
          </div>
        </div>
        <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4 border-2 border-indigo-200" data-id="element-1386">
          <div className="text-sm font-semibold text-indigo-900 mb-1" data-id="element-1387">
            Images
          </div>
          <div className="text-2xl font-bold text-indigo-700" data-id="element-1388">
            {stats.totalImages}
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 border-2 border-emerald-200" data-id="element-1389">
          <div className="text-sm font-semibold text-emerald-900 mb-1" data-id="element-1390">
            Promotion
          </div>
          <div className="text-2xl font-bold text-emerald-700" data-id="element-1391">
            {stats.promotion}
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 border-2 border-amber-200" data-id="element-1392">
          <div className="text-sm font-semibold text-amber-900 mb-1" data-id="element-1393">
            Prevention
          </div>
          <div className="text-2xl font-bold text-amber-700" data-id="element-1394">
            {stats.prevention}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 mb-6" data-id="element-1395">
        <div className="flex items-center justify-between mb-4" data-id="element-1396">
          <div className="flex items-center gap-2" data-id="element-1397">
            <FilterIcon className="w-5 h-5 text-gray-600" data-id="element-1398" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-1399">Filters</h2>
            {hasActiveFilters && <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full" data-id="element-1400">
                Active
              </span>}
          </div>
          <div className="flex items-center gap-2" data-id="element-1401">
            {hasActiveFilters && <button onClick={clearFilters} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1" data-id="element-1402">
                <XIcon className="w-4 h-4" data-id="element-1403" />
                Clear
              </button>}
            <button onClick={() => setShowFilters(!showFilters)} className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1404">
              {showFilters ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>
        {showFilters && <div className="space-y-4" data-id="element-1405">
            {/* Search - NOW USING SHARED COMPONENT */}
            <div data-id="element-1406">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1407">
                Search
              </label>
              <SearchInput value={searchQuery} onChange={setSearchQuery} placeholder="Search by name, description, or ID..." data-id="element-1408" />
            </div>
            {/* Filter Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1409">
              {/* Emotional Category Filter */}
              <div data-id="element-1410">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1411">
                  Emotional Category
                </label>
                <select value={selectedEmotionalCategory} onChange={e => setSelectedEmotionalCategory(e.target.value as EmotionalCategory | 'all')} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" data-id="element-1412">
                  <option value="all" data-id="element-1413">All Categories</option>
                  <option value="promotion" data-id="element-1414">Promotion</option>
                  <option value="prevention" data-id="element-1415">Prevention</option>
                </select>
              </div>
              {/* Status Filter */}
              <div data-id="element-1416">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1417">
                  Status
                </label>
                <select value={selectedStatus} onChange={e => setSelectedStatus(e.target.value as 'all' | 'published' | 'draft')} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" data-id="element-1418">
                  <option value="all" data-id="element-1419">All Status</option>
                  <option value="published" data-id="element-1420">Published</option>
                  <option value="draft" data-id="element-1421">Draft</option>
                </select>
              </div>
              {/* Age Band Filter */}
              <div data-id="element-1422">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1423">
                  Age Band
                </label>
                <select value={selectedAgeBand} onChange={e => setSelectedAgeBand(e.target.value as AgeBand | 'all')} className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all" data-id="element-1424">
                  <option value="all" data-id="element-1425">All Age Bands</option>
                  <option value="5-9" data-id="element-1426">5-9 years</option>
                  <option value="10-14" data-id="element-1427">10-14 years</option>
                  <option value="14-18" data-id="element-1428">14-18 years</option>
                  <option value="Adults" data-id="element-1429">Adults</option>
                  <option value="Students" data-id="element-1430">Students</option>
                </select>
              </div>
            </div>
          </div>}
      </div>

      {/* Image Sets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1431">
        {filteredSets.map(set => {
        const coverage = getGridCoverage(set);
        const emotionalDist = getSetEmotionalDistribution(set);
        return <div key={set.id} className="bg-white rounded-xl shadow-sm border-2 border-gray-200 hover:border-purple-300 hover:shadow-md transition-all overflow-hidden" data-id="element-1432">
              {/* Header */}
              <div className="p-6 border-b border-gray-200" data-id="element-1433">
                <div className="flex items-start justify-between mb-3" data-id="element-1434">
                  <div className="flex-1" data-id="element-1435">
                    <div className="flex items-center gap-2 mb-2" data-id="element-1436">
                      <h3 className="text-lg font-bold text-gray-900" data-id="element-1437">
                        {set.name}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-bold rounded-full ${set.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-1438">
                        {set.status}
                      </span>
                    </div>
                    {set.description && <p className="text-sm text-gray-600 mb-2" data-id="element-1439">
                        {set.description}
                      </p>}
                    <div className="text-xs text-gray-500 font-mono" data-id="element-1440">
                      {set.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-1441">
                    <button onClick={() => setSelectedSet(set)} className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors" title="View details" data-id="element-1442">
                      <EyeIcon className="w-4 h-4" data-id="element-1443" />
                    </button>
                    <button onClick={() => openEditModal(set)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-id="element-1444">
                      <EditIcon className="w-4 h-4" data-id="element-1445" />
                    </button>
                    <button onClick={() => openDeleteConfirm(set)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-1446">
                      <TrashIcon className="w-4 h-4" data-id="element-1447" />
                    </button>
                  </div>
                </div>

                {/* Emotional Category Distribution */}
                <div className="flex items-center gap-3 mb-3" data-id="element-1448">
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-lg" data-id="element-1449">
                    <div className="w-2 h-2 bg-green-500 rounded-full" data-id="element-1450"></div>
                    <span className="text-sm font-semibold text-green-700" data-id="element-1451">
                      Promotion: {emotionalDist.promotion}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 rounded-lg" data-id="element-1452">
                    <div className="w-2 h-2 bg-orange-500 rounded-full" data-id="element-1453"></div>
                    <span className="text-sm font-semibold text-orange-700" data-id="element-1454">
                      Prevention: {emotionalDist.prevention}
                    </span>
                  </div>
                </div>

                {/* Grid Coverage */}
                <div className="mb-3" data-id="element-1455">
                  <div className="flex items-center justify-between mb-2" data-id="element-1456">
                    <span className="text-sm font-semibold text-gray-700" data-id="element-1457">
                      Grid Coverage
                    </span>
                    <span className="text-sm font-bold text-purple-600" data-id="element-1458">
                      {coverage.percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1459">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all" style={{
                  width: `${coverage.percentage}%`
                }} data-id="element-1460" />
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-1461">
                    {coverage.filled} of {coverage.total} cells filled
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-xs text-gray-600" data-id="element-1462">
                  <div className="flex items-center gap-1" data-id="element-1463">
                    <LayersIcon className="w-3 h-3" data-id="element-1464" />
                    <span data-id="element-1465">{set.images.length} images</span>
                  </div>
                  <div data-id="element-1466">v{set.version}</div>
                  <div data-id="element-1467">{set.ageBands.join(', ')}</div>
                </div>
              </div>

              {/* No Repeat Policy */}
              {set.noRepeatPolicy?.enabled && <div className="px-6 py-3 bg-blue-50 border-t border-blue-100" data-id="element-1468">
                  <div className="flex items-center gap-2" data-id="element-1469">
                    <AlertCircleIcon className="w-4 h-4 text-blue-600" data-id="element-1470" />
                    <span className="text-sm font-semibold text-blue-700" data-id="element-1471">
                      No-Repeat Policy: {set.noRepeatPolicy.windowDays} day
                      window
                    </span>
                  </div>
                </div>}
            </div>;
      })}
      </div>

      {/* Empty State */}
      {filteredSets.length === 0 && <div className="text-center py-12" data-id="element-1472">
          <LayersIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" data-id="element-1473" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-1474">
            No image sets found
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-1475">
            {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first image set to get started'}
          </p>
        </div>}

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && <ImageSetModal isOpen={showCreateModal || showEditModal} onClose={() => {
      setShowCreateModal(false);
      setShowEditModal(false);
      setEditingSet(null);
    }} onSave={showCreateModal ? handleCreateSet : handleUpdateSet} editingSet={editingSet} allImages={MOCK_IMAGES} data-id="element-1476" />}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingSet && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1477">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6" data-id="element-1478">
            <div className="flex items-center gap-3 mb-4" data-id="element-1479">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center" data-id="element-1480">
                <AlertCircleIcon className="w-6 h-6 text-red-600" data-id="element-1481" />
              </div>
              <div data-id="element-1482">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1483">
                  Delete Image Set
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1484">
                  This action cannot be undone
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-700 mb-6" data-id="element-1485">
              Are you sure you want to delete "{deletingSet.name}"? This will
              permanently remove the image set and cannot be recovered.
            </p>
            <div className="flex items-center gap-3" data-id="element-1486">
              <button onClick={() => {
            setShowDeleteConfirm(false);
            setDeletingSet(null);
          }} className="flex-1 px-4 py-2.5 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold" data-id="element-1487">
                Cancel
              </button>
              <button onClick={handleDeleteSet} className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold" data-id="element-1488">
                Delete
              </button>
            </div>
          </div>
        </div>}

      {/* Set Details Modal */}
      {selectedSet && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-1489">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1490">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between" data-id="element-1491">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-1492">
                {selectedSet.name}
              </h2>
              <button onClick={() => setSelectedSet(null)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1493">
                <XIcon className="w-6 h-6 text-gray-600" data-id="element-1494" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6" data-id="element-1495">
              <div className="grid grid-cols-4 gap-4" data-id="element-1496">
                {getSetImages(selectedSet).map(image => <div key={image.id} className="relative group rounded-lg overflow-hidden border-2 border-gray-200 hover:border-purple-400 hover:shadow-md transition-all" data-id="element-1497">
                    <div className="aspect-square bg-gray-100 relative" data-id="element-1498">
                      {image.url ? <img src={image.thumbnailUrl || image.url} alt={image.filename} className="w-full h-full object-cover" data-id="element-1499" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-1500">
                          <LayersIcon className="w-8 h-8 text-gray-400" data-id="element-1501" />
                        </div>}
                      <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold ${image.emotionalCategory === 'promotion' ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'}`} data-id="element-1502">
                        {image.emotionalCategory === 'promotion' ? '+' : '-'}
                      </div>
                    </div>
                    <div className="p-2" data-id="element-1503">
                      <div className="text-xs font-semibold text-gray-900 truncate" data-id="element-1504">
                        {image.filename}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1505">
                        {image.domain} - {image.level}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
/**
 * Image Set Create/Edit Modal Component
 */
interface ImageSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (set: any) => void;
  editingSet: ImageSet | null;
  allImages: ImageMetadata[];
}
function ImageSetModal({
  isOpen,
  onClose,
  onSave,
  editingSet,
  allImages
}: ImageSetModalProps) {
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    status: 'draft' | 'published';
    ageBands: AgeBand[];
    locales: string[];
    images: string[];
    grid: {
      [domain: string]: {
        [level: string]: string[];
      };
    };
    noRepeatPolicy: {
      enabled: boolean;
      windowDays?: number;
    };
  }>(editingSet || {
    name: '',
    description: '',
    status: 'draft',
    ageBands: [],
    locales: ['en'],
    images: [],
    grid: {},
    noRepeatPolicy: {
      enabled: false,
      windowDays: 30
    }
  });
  const [selectedImages, setSelectedImages] = useState<Set<string>>(new Set(editingSet?.images || []));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      images: Array.from(selectedImages)
    });
  };
  const toggleImageSelection = (imageId: string) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(imageId)) {
      newSelected.delete(imageId);
    } else {
      newSelected.add(imageId);
    }
    setSelectedImages(newSelected);
  };
  const toggleAgeBand = (band: AgeBand) => {
    setFormData(prev => ({
      ...prev,
      ageBands: prev.ageBands.includes(band) ? prev.ageBands.filter(b => b !== band) : [...prev.ageBands, band]
    }));
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-id="element-1506">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full my-8" data-id="element-1507">
        <form onSubmit={handleSubmit} data-id="element-1508">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10" data-id="element-1509">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-1510">
              {editingSet ? 'Edit Image Set' : 'Create Image Set'}
            </h2>
            <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1511">
              <XIcon className="w-6 h-6 text-gray-600" data-id="element-1512" />
            </button>
          </div>
          {/* Form Content */}
          <div className="p-6 space-y-6 max-h-[calc(90vh-200px)] overflow-y-auto" data-id="element-1513">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-6" data-id="element-1514">
              <div data-id="element-1515">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1516">
                  Name *
                </label>
                <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} required className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="e.g., Emotional Faces Set A" data-id="element-1517" />
              </div>
              <div data-id="element-1518">
                <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1519">
                  Status
                </label>
                <select value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as 'draft' | 'published'
              })} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500" data-id="element-1520">
                  <option value="draft" data-id="element-1521">Draft</option>
                  <option value="published" data-id="element-1522">Published</option>
                </select>
              </div>
            </div>
            <div data-id="element-1523">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1524">
                Description
              </label>
              <textarea value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} rows={3} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500" placeholder="Describe this image set..." data-id="element-1525" />
            </div>
            {/* Age Bands */}
            <div data-id="element-1526">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1527">
                Age Bands
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-1528">
                {(['5-9', '10-14', '14-18', 'Adults', 'Students'] as AgeBand[]).map(band => <button key={band} type="button" onClick={() => toggleAgeBand(band)} className={`px-4 py-2 rounded-lg border-2 font-semibold transition-all ${formData.ageBands.includes(band) ? 'bg-purple-100 border-purple-500 text-purple-700' : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'}`} data-id="element-1529">
                    {band}
                  </button>)}
              </div>
            </div>
            {/* No-Repeat Policy */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-1530">
              <div className="flex items-center justify-between mb-3" data-id="element-1531">
                <label className="text-sm font-semibold text-blue-900" data-id="element-1532">
                  Enable No-Repeat Policy
                </label>
                <input type="checkbox" checked={formData.noRepeatPolicy.enabled} onChange={e => setFormData({
                ...formData,
                noRepeatPolicy: {
                  ...formData.noRepeatPolicy,
                  enabled: e.target.checked
                }
              })} className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500" data-id="element-1533" />
              </div>
              {formData.noRepeatPolicy.enabled && <div data-id="element-1534">
                  <label className="block text-sm font-semibold text-blue-900 mb-2" data-id="element-1535">
                    Window (days)
                  </label>
                  <input type="number" value={formData.noRepeatPolicy.windowDays} onChange={e => setFormData({
                ...formData,
                noRepeatPolicy: {
                  ...formData.noRepeatPolicy,
                  windowDays: parseInt(e.target.value) || 30
                }
              })} min={1} className="w-32 px-4 py-2 border-2 border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-1536" />
                </div>}
            </div>
            {/* Image Selection */}
            <div data-id="element-1537">
              <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-1538">
                Select Images ({selectedImages.size} selected)
              </label>
              <div className="grid grid-cols-4 gap-4 max-h-96 overflow-y-auto p-4 bg-gray-50 rounded-lg border-2 border-gray-200" data-id="element-1539">
                {allImages.map(image => <div key={image.id} onClick={() => toggleImageSelection(image.id)} className={`relative group rounded-lg overflow-hidden border-2 cursor-pointer transition-all ${selectedImages.has(image.id) ? 'border-purple-500 shadow-lg' : 'border-gray-200 hover:border-purple-300'}`} data-id="element-1540">
                    <div className="aspect-square bg-gray-100 relative" data-id="element-1541">
                      {image.url ? <img src={image.thumbnailUrl || image.url} alt={image.filename} className="w-full h-full object-cover" data-id="element-1542" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-1543">
                          <ImageIcon className="w-8 h-8 text-gray-400" data-id="element-1544" />
                        </div>}
                      {selectedImages.has(image.id) && <div className="absolute inset-0 bg-purple-600/20 flex items-center justify-center" data-id="element-1545">
                          <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center" data-id="element-1546">
                            <CheckIcon className="w-5 h-5 text-white" data-id="element-1547" />
                          </div>
                        </div>}
                      <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-bold ${image.emotionalCategory === 'promotion' ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'}`} data-id="element-1548">
                        {image.emotionalCategory === 'promotion' ? '+' : '-'}
                      </div>
                    </div>
                    <div className="p-2 bg-white" data-id="element-1549">
                      <div className="text-xs font-semibold text-gray-900 truncate" data-id="element-1550">
                        {image.filename}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1551">
                        {image.domain} - {image.level}
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
          {/* Footer */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3 sticky bottom-0 bg-white" data-id="element-1552">
            <button type="button" onClick={onClose} className="px-6 py-2.5 border-2 border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold" data-id="element-1553">
              Cancel
            </button>
            <button type="submit" disabled={!formData.name || selectedImages.size === 0} className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all font-semibold shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1554">
              <SaveIcon className="w-5 h-5" data-id="element-1555" />
              {editingSet ? 'Save Changes' : 'Create Set'}
            </button>
          </div>
        </form>
      </div>
    </div>;
}